import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

type PdfPreviewProps = {
  src: string;
  title: string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
};

const PdfPreview: React.FC<PdfPreviewProps> = ({
  src,
  title,
  height = 720,
  className = '',
  style = {},
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resolvedHeight = typeof height === 'number' ? `${height}px` : height;

  useEffect(() => {
    let cancelled = false;

    const renderFirstPage = async () => {
      try {
        setLoading(true);
        setError(null);

        const pdf = await pdfjsLib.getDocument(src).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.25 });
        const canvas = canvasRef.current;

        if (!canvas || cancelled) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Canvas context not available');
        }

        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));

        await page.render({
          canvas,
          canvasContext: ctx,
          viewport,
        }).promise;
      } catch (err) {
        if (!cancelled) {
          setError('Unable to render PDF preview.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    renderFirstPage();

    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <div
      className={className}
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        padding: '5px 5px 0',
        ...style,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 690,
          background: 'linear-gradient(180deg, #f8f9fb 0%, #f2f4f8 100%)',
          border: '1px solid rgba(17, 24, 39, 0.08)',
          borderRadius: '14px',
          boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04)',
          padding: '5px',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '100%',
            height: resolvedHeight,
            borderRadius: '10px',
            overflow: 'hidden',
            background: '#ffffff',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {error ? (
            <div style={{ padding: '1rem', color: '#b42318', fontWeight: 600, textAlign: 'center' }}>
              {error}
            </div>
          ) : (
            <canvas
              ref={canvasRef}
              title={title}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: '100%',
                background: '#ffffff',
                objectFit: 'contain',
              }}
            />
          )}

          {loading && !error && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#032d60',
                fontWeight: 600,
                background: 'rgba(255,255,255,0.7)',
              }}
            >
              Loading certificate...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfPreview;
